import type { QueryShape } from '@aibi/analysis-engine';
import type { EncodingChannel, VisualizationType } from '@aibi/contracts';
import { Registry } from '@aibi/shared';

export interface VisualizationRequirements {
  readonly minDimensions: number;
  readonly maxDimensions: number;
  readonly minMeasures: number;
  readonly maxMeasures: number;
  readonly requiresTemporal?: boolean;
  readonly requiresGeographic?: boolean;
}

/** Descripcion declarativa de un tipo de visualizacion (integrado o de plugin). */
export interface VisualizationDescriptor {
  readonly id: VisualizationType;
  readonly label: string;
  readonly category:
    'table' | 'single-value' | 'comparison' | 'trend' | 'composition' | 'distribution' | 'geo';
  readonly channels: readonly EncodingChannel[];
  readonly requirements: VisualizationRequirements;
}

const ANY = Number.POSITIVE_INFINITY;

export const BUILTIN_VISUALIZATIONS: readonly VisualizationDescriptor[] = [
  {
    id: 'table',
    label: 'Tabla',
    category: 'table',
    channels: ['columns'],
    requirements: { minDimensions: 0, maxDimensions: ANY, minMeasures: 0, maxMeasures: ANY },
  },
  {
    id: 'grouped-table',
    label: 'Tabla agrupada',
    category: 'table',
    channels: ['rows', 'values'],
    requirements: { minDimensions: 1, maxDimensions: ANY, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'pivot',
    label: 'Tabla pivote',
    category: 'table',
    channels: ['rows', 'columns', 'values'],
    requirements: { minDimensions: 2, maxDimensions: ANY, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'kpi',
    label: 'KPI',
    category: 'single-value',
    channels: ['values'],
    requirements: { minDimensions: 0, maxDimensions: 0, minMeasures: 1, maxMeasures: 4 },
  },
  {
    id: 'bar',
    label: 'Barras',
    category: 'comparison',
    channels: ['x', 'y', 'color', 'tooltip'],
    requirements: { minDimensions: 1, maxDimensions: 2, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'column',
    label: 'Columnas',
    category: 'comparison',
    channels: ['x', 'y', 'color', 'tooltip'],
    requirements: { minDimensions: 1, maxDimensions: 2, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'line',
    label: 'Lineas',
    category: 'trend',
    channels: ['x', 'y', 'color', 'tooltip'],
    requirements: { minDimensions: 1, maxDimensions: 2, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'area',
    label: 'Area',
    category: 'trend',
    channels: ['x', 'y', 'color', 'tooltip'],
    requirements: { minDimensions: 1, maxDimensions: 2, minMeasures: 1, maxMeasures: ANY },
  },
  {
    id: 'pie',
    label: 'Circular',
    category: 'composition',
    channels: ['color', 'values', 'label'],
    requirements: { minDimensions: 1, maxDimensions: 1, minMeasures: 1, maxMeasures: 1 },
  },
  {
    id: 'donut',
    label: 'Anillo',
    category: 'composition',
    channels: ['color', 'values', 'label'],
    requirements: { minDimensions: 1, maxDimensions: 1, minMeasures: 1, maxMeasures: 1 },
  },
  {
    id: 'scatter',
    label: 'Dispersion',
    category: 'distribution',
    channels: ['x', 'y', 'size', 'color', 'detail'],
    requirements: { minDimensions: 0, maxDimensions: 2, minMeasures: 2, maxMeasures: 3 },
  },
  {
    id: 'treemap',
    label: 'Mapa de arbol',
    category: 'composition',
    channels: ['detail', 'size', 'color'],
    requirements: { minDimensions: 1, maxDimensions: 3, minMeasures: 1, maxMeasures: 2 },
  },
  {
    id: 'waterfall',
    label: 'Cascada',
    category: 'composition',
    channels: ['x', 'y'],
    requirements: { minDimensions: 1, maxDimensions: 1, minMeasures: 1, maxMeasures: 1 },
  },
  {
    id: 'map',
    label: 'Mapa',
    category: 'geo',
    channels: ['geo', 'color', 'size', 'tooltip'],
    requirements: {
      minDimensions: 1,
      maxDimensions: 2,
      minMeasures: 1,
      maxMeasures: 2,
      requiresGeographic: true,
    },
  },
  {
    id: 'heatmap',
    label: 'Mapa de calor',
    category: 'distribution',
    channels: ['x', 'y', 'color'],
    requirements: { minDimensions: 2, maxDimensions: 2, minMeasures: 1, maxMeasures: 1 },
  },
];

/** Registro de visualizaciones con las integradas precargadas; los plugins agregan las suyas. */
export function createVisualizationRegistry(): Registry<VisualizationDescriptor> {
  const registry = new Registry<VisualizationDescriptor>('visualizacion');
  for (const descriptor of BUILTIN_VISUALIZATIONS) registry.register(descriptor);
  return registry;
}

export interface CompatibilityResult {
  readonly compatible: boolean;
  readonly reasons: readonly string[];
}

/** Evalua si una visualizacion puede representar una consulta con la forma dada. */
export function checkCompatibility(
  descriptor: VisualizationDescriptor,
  shape: QueryShape,
): CompatibilityResult {
  const { requirements: req } = descriptor;
  const reasons: string[] = [];
  if (shape.dimensionCount < req.minDimensions)
    reasons.push(`Requiere al menos ${req.minDimensions} dimension(es).`);
  if (shape.dimensionCount > req.maxDimensions)
    reasons.push(`Admite como maximo ${req.maxDimensions} dimension(es).`);
  if (shape.measureCount < req.minMeasures)
    reasons.push(`Requiere al menos ${req.minMeasures} medida(s).`);
  if (shape.measureCount > req.maxMeasures)
    reasons.push(`Admite como maximo ${req.maxMeasures} medida(s).`);
  if (req.requiresTemporal === true && shape.temporalDimensionCount === 0)
    reasons.push('Requiere una dimension temporal.');
  if (req.requiresGeographic === true && shape.geographicDimensionCount === 0)
    reasons.push('Requiere una dimension geografica.');
  return { compatible: reasons.length === 0, reasons };
}

export function listCompatibleVisualizations(
  registry: Registry<VisualizationDescriptor>,
  shape: QueryShape,
): VisualizationDescriptor[] {
  return registry.list().filter((descriptor) => checkCompatibility(descriptor, shape).compatible);
}
